import React from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import headerBg from "./assets/picOne.png";

// Custom orange marker (#ff6c2f) using SVG
const orangeIcon = new L.DivIcon({
  className: "",
  html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 40" width="30" height="45">
    <path fill="#ff6c2f" stroke="black" stroke-width="1" d="M12 0C7.031 0 3 4.031 3 9c0 7.875 9 31 9 31s9-23.125 9-31c0-4.969-4.031-9-9-9z"/>
    <circle fill="white" cx="12" cy="9" r="4"/>
  </svg>`,
  iconSize: [30, 45],
  iconAnchor: [15, 45],
  popupAnchor: [0, -40],
});

// Office data with coordinates
const offices = [
  { name: "Head Office", location: "Blantyre", coords: [-15.7861, 35.0058] },
  { name: "Lilongwe Regional Office", location: "Lilongwe", coords: [-13.9669, 33.7873] },
  { name: "Mzuzu Regional Office", location: "Mzuzu", coords: [-11.4404, 34.0170] },
  { name: "Kamuzu International Airport Satellite Office", location: "Lilongwe", coords: [-13.7895, 33.7806] },
  { name: "Chileka International Airport Satellite Office", location: "Blantyre", coords: [-15.6706, 34.9900] },
  { name: "Mloza Border Office", location: "Mulanje", coords: [-16.0333, 35.6000] },
  { name: "Dedza Border Office", location: "Dedza", coords: [-14.3500, 34.3000] },
  { name: "Mchinji Border Office", location: "Mchinji", coords: [-13.7700, 33.4500] },
  { name: "Songwe Border Office", location: "Kalonga", coords: [-9.941614, 33.924394] },
  { name: "Chitipa Border Office", location: "Chitipa", coords: [-9.7000, 33.3000] },
  { name: "Mwanza Border Office", location: "Mwanza", coords: [-15.5000, 34.8000] },
];

// Component to set map bounds to Malawi
const MalawiBounds = () => {
  const map = useMap();
  const southWest = L.latLng(-17.1, 32.5);
  const northEast = L.latLng(-9.2, 35.9);
  map.fitBounds([southWest, northEast]);
  return null;
};

const OfficesStaffing = () => {
  return (
    <div className="about-us">
      {/* Header */}
      <section
        className="about-header"
        style={{
          backgroundImage: `url(${headerBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: "45vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="overlay">
          <h1 className="page-title">Offices & Staffing</h1>
          <p className="subtitle">
            The MBS operates multiple offices across the country to deliver standards, testing, metrology, and related services.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="content-section">
        <div className="content-inner">
          <p className="intro-line">
            The Malawi Bureau of Standards maintains a network of offices to serve industry, government, and citizens across Malawi.
          </p>

          <h2 className="section-title theme-color-text">Offices</h2>

          <div className="two-column">
            {/* LEFT: Offices Table */}
            <div className="table-wrap">
              <table className="offices-table">
                <thead>
                  <tr className="table-header">
                    <th>Office</th>
                    <th>District Location</th>
                  </tr>
                </thead>
                <tbody>
                  {offices.map((office, idx) => (
                    <tr key={idx}>
                      <td>{office.name}</td>
                      <td>{office.location}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* RIGHT: Leaflet Map */}
            <div className="map-wrapper">
              <MapContainer
                center={[-13.5, 34.0]}
                zoom={7}
                minZoom={6}
                maxZoom={9}
                style={{ height: "100%", width: "100%", borderRadius: "15px" }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                <MalawiBounds />
                {offices.map((office, idx) => (
                  <Marker key={idx} position={office.coords} icon={orangeIcon}>
                    <Popup>
                      <strong>{office.name}</strong>
                      <br />
                      {office.location}
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>

          <h2 className="section-title theme-color-text">Staffing</h2>
          <p className="staffing-paragraph">
            The MBS has a staff complement of <strong>192</strong> distributed across the offices and functions of the organization.
          </p>
        </div>
      </section>

      {/* Styles */}
      <style>{`
        :root {
          --accent: #ff6c2f;
          --muted: #666;
          --card-bg: #ffffff;
          --page-bg: #f7f7f8;
        }
        .about-us { font-family: Inter, sans-serif; background: var(--page-bg); color: #222; }
        .overlay {
          background: rgba(0,0,0,0.55);
          padding: 32px;
          text-align: center;
          color: white;
          width: 92%;
          max-width: 1200px;
          border-radius: 12px;
          border-top: 6px solid var(--accent);
        }
        .page-title { font-size: 2.2rem; font-weight: 700; }
        .subtitle { color: rgba(255,255,255,0.92); }
        .content-section { padding: 40px 20px 80px; }
        .content-inner { max-width: 1200px; margin: auto; }
        .two-column { display: flex; gap: 30px; align-items: flex-start; margin-bottom: 40px; }
        .offices-table {
          width: 100%; border-collapse: collapse; background: var(--card-bg); border-radius: 12px; overflow: hidden; box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .table-header { background: var(--accent); color: white; }
        td, th { padding: 16px 20px; }
        .map-wrapper {
          width: 50%; min-width: 300px; height: 480px; border-radius: 15px; box-shadow: 0 12px 30px rgba(0,0,0,0.15); overflow: hidden;
        }
        @media (max-width: 900px) { .two-column { flex-direction: column; } .map-wrapper { width: 100%; height: 400px; } }
        .section-title { margin: 32px 0 16px; font-weight: 700; }
        .theme-color-text { color: var(--accent); }
        .intro-line, .staffing-paragraph { font-size: 1rem; line-height: 1.6; color: var(--muted); }
      `}</style>
    </div>
  );
};

export default OfficesStaffing;
